import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import type {
  MainCouranteEntree,
  MainCouranteKind,
  Vacation,
} from "@victor/api-client";
import { ApiClientError } from "@victor/api-client";
import { api } from "../../lib/api";
import { tryGetCurrentPosition } from "../../lib/geolocation";
import { enqueueOrSend } from "../../lib/sync-manager";

/**
 * Vague 1 · Phase C.3.a · Bloc main courante de la VacationCard.
 *
 * Affiche un résumé compact (n entrées + dernière en aperçu) avec bouton
 * « + Ajouter une entrée » qui ouvre une bottom sheet pleine largeur :
 * choix du kind, textarea, envoi avec géoloc.
 *
 * Append-only : pas de bouton Éditer/Supprimer. Erreur de frappe = nouvelle
 * entrée correctrice (preuve opposable, comme un cahier papier).
 */
export function MainCouranteBlock({ vacation }: { vacation: Vacation }) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const entries = useQuery({
    queryKey: ["me-main-courante", vacation.id],
    queryFn: () => api.me.mainCouranteList(vacation.id),
    enabled: vacation.statut !== "annulee",
  });

  if (vacation.statut === "annulee") return null;

  const list = entries.data ?? [];
  const last = list[list.length - 1] ?? null;

  return (
    <div className="mt-3 border-t border-navy-100 pt-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-navy-500">
          Main courante
        </p>
        <span className="text-xs font-semibold text-navy-400">
          {list.length} {list.length > 1 ? "entrées" : "entrée"}
        </span>
      </div>

      {last ? (
        <article className="mt-2 rounded-xl bg-navy-50 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <KindBadge kind={last.kind} />
            <span className="text-xs text-navy-500">
              {formatTime(last.occurredAt)}
            </span>
          </div>
          <p className="mt-1 line-clamp-2 text-sm text-navy-800">
            {last.texte}
          </p>
        </article>
      ) : null}

      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-accent-300 px-3 py-2.5 text-sm font-semibold text-accent-600 transition-colors hover:bg-accent-50"
      >
        <PlusIcon />
        Ajouter une entrée
      </button>

      <AnimatePresence>
        {sheetOpen ? (
          <AddEntrySheet
            vacationId={vacation.id}
            onClose={() => setSheetOpen(false)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------

function AddEntrySheet({
  vacationId,
  onClose,
}: {
  vacationId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<MainCouranteKind>("observation");
  const [texte, setTexte] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutate = useMutation({
    mutationFn: async () => {
      const trimmed = texte.trim();
      if (trimmed.length === 0) throw new Error("EMPTY");
      const { position } = await tryGetCurrentPosition({ timeoutMs: 6000 });
      const payload = {
        vacationId,
        kind,
        texte: trimmed,
        latitude: position?.latitude ?? null,
        longitude: position?.longitude ?? null,
        precisionMetres: position?.precisionMetres ?? null,
        occurredClientAt: new Date().toISOString(),
      };
      return enqueueOrSend(
        "main_courante",
        payload,
        { vacationId },
        () => api.me.mainCourante(payload),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me-main-courante", vacationId] });
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof Error && err.message === "EMPTY") {
        setError("Écrivez quelque chose avant d'envoyer.");
        return;
      }
      setError(formatErr(err));
    },
  });

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-navy-900/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label="Nouvelle entrée de main courante"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 32 }}
        className="fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-y-auto rounded-t-3xl bg-white px-5 pb-8 pt-4 shadow-2xl"
      >
        {/* Drag handle */}
        <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-navy-200" />

        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-black text-navy-800">
            Nouvelle entrée
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-navy-500 hover:bg-navy-50"
            aria-label="Fermer"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Kind selector */}
        <div className="mt-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-navy-500">
            Type
          </p>
          <div className="grid grid-cols-3 gap-2">
            {(["observation", "evenement", "incident"] as const).map((k) => {
              const active = kind === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`rounded-xl px-3 py-2 text-xs font-bold capitalize transition-colors ${
                    active
                      ? k === "incident"
                        ? "bg-red-500 text-white"
                        : k === "evenement"
                          ? "bg-amber-500 text-white"
                          : "bg-accent-500 text-white"
                      : "bg-navy-50 text-navy-700"
                  }`}
                >
                  {k === "evenement" ? "Événement" : k}
                </button>
              );
            })}
          </div>
        </div>

        {/* Textarea */}
        <div className="mt-4">
          <textarea
            autoFocus
            value={texte}
            onChange={(e) => {
              setTexte(e.target.value);
              setError(null);
            }}
            rows={6}
            placeholder="Décrivez ce que vous observez…"
            maxLength={4000}
            className="w-full resize-none rounded-xl border border-navy-200 bg-white px-3 py-3 text-base text-navy-800 placeholder:text-navy-400 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-100"
          />
          <p className="mt-1 text-right text-xs text-navy-400">
            {texte.length} / 4000
          </p>
        </div>

        {/* Erreur */}
        {error ? (
          <p className="mt-2 rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-800">
            {error}
          </p>
        ) : null}

        {/* Actions */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={mutate.isPending}
            className="rounded-xl bg-navy-50 px-4 py-3 text-sm font-semibold text-navy-700 hover:bg-navy-100 disabled:opacity-60"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={mutate.isPending || texte.trim().length === 0}
            onClick={() => mutate.mutate()}
            className="flex items-center justify-center gap-2 rounded-xl bg-accent-500 px-4 py-3 text-sm font-bold text-white shadow-sm hover:bg-accent-600 disabled:opacity-60"
          >
            {mutate.isPending ? <Spinner /> : "Enregistrer"}
          </button>
        </div>
      </motion.div>
    </>
  );
}

// ---------------------------------------------------------------------------

function KindBadge({ kind }: { kind: MainCouranteEntree["kind"] }) {
  const map: Record<
    MainCouranteEntree["kind"],
    { label: string; cls: string }
  > = {
    observation: { label: "Observation", cls: "bg-accent-100 text-accent-700" },
    evenement: { label: "Événement", cls: "bg-amber-100 text-amber-800" },
    incident: { label: "Incident", cls: "bg-red-100 text-red-800" },
  };
  const { label, cls } = map[kind];
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}
    >
      {label}
    </span>
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiClientError) {
    switch (err.code) {
      case "not_your_vacation":
        return "Cette vacation ne vous est pas affectée.";
      case "vacation_annulee":
        return "Vacation annulée, main courante figée.";
      case "agent_not_found":
        return "Votre compte n'est pas relié à un agent terrain.";
      case "forbidden":
        return "Vous n'avez pas la permission d'écrire.";
      default:
        return err.message || "Échec de l'enregistrement.";
    }
  }
  return "Échec de l'enregistrement. Vérifiez votre connexion.";
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------

function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      aria-hidden="true"
      className="animate-spin"
    >
      <circle cx="12" cy="12" r="9" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" />
    </svg>
  );
}
